import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ExperienceForm from './ExperienceForm';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { format } from 'date-fns';
import SimpleBar from 'simplebar-react';
import 'simplebar/dist/simplebar.min.css';

interface ExperienceProps {
  isLoggedIn: boolean;
  experiences: Experiences[];
}
export interface Experiences {
  id?: number;
  title: string;
  company: string;
  place: string;
  start_date: string;
  end_date: string | null;
  stack: string[];
  details: string;
}

const Experience: React.FC<ExperienceProps> = ({ isLoggedIn, experiences }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // const { data: experiences = [], error, isLoading } = useGetExperiencesQuery();

  // if (isLoading) return <CircularProgress />;
  // if (error) return <Typography variant="h6">Error loading portfolio items</Typography>;

  const handleEdit = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const groupByCompany = (exps: Experiences[]) =>
    exps.reduce((acc, experience) => {
      const { company, place, start_date } = experience;
      const key = `${company} - ${place} - ${start_date}`;
      if (!acc[key]) {
        acc[key] = { experiences: [], company, place, start_date, end_date: experience.end_date };
      }
      acc[key].experiences.push(experience);
      return acc;
    }, {} as Record<string, { experiences: Experiences[]; company: string; place: string; start_date: string; end_date: string | null }>);

  const groupedValue = Object.values(groupByCompany(experiences));

  return (
    <Box
      sx={{
        backgroundColor: '#4a4f54',
        p: 4,
        position: 'relative',
        borderRadius: 4,
      }}
    >
      {/* Title & Edit Button */}
      <Typography variant="h4" color="white" sx={{ position: 'absolute', top: 8, left: 16 }}>
        {t('experience')}
      </Typography>
      {isLoggedIn && (
        <IconButton
          onClick={handleEdit}
          sx={{ position: 'absolute', top: 8, right: 16, color: 'white', bgcolor: '#282c34' }}
        >
          <EditIcon />
        </IconButton>
      )}
      <Divider
        sx={{
          mt: '30px',
          width: '103%',
          bgcolor: 'white',
          alignSelf: 'center',
        }}
      />

      {/* Scrollable List with SimpleBar */}
      <Box sx={{ mt: 4, width: '90%', maxWidth: '800px', mx: 'auto', color: 'white' }}>
        <SimpleBar
          autoHide={false}
          style={{
            maxHeight: window.innerWidth < 600 ? 300 : 672,
            width: '100%',
            paddingRight: '42px'
          }}
        >
           <Box sx={{ textAlign: 'left' }}>
          {groupedValue.map(({ experiences, company, place, start_date, end_date }, index) => (
            <Box key={`${company}-${start_date}`} sx={{ mb: 2 }}>
              <Typography variant="h6">
                {experiences[0].title} ({company} – {place},{' '}
                {format(new Date(start_date), 'MMMM yyyy')}
                {end_date ? ` - ${format(new Date(end_date), 'MMMM yyyy')}` : ' - Present'})
              </Typography>
              {experiences.map((exp) => (
                <Box key={exp.id} sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {exp.details}
                  </Typography>
                  <Typography variant="body2">Stack: {exp.stack.join(', ')}</Typography>
                </Box>
              ))}
              {index < groupedValue.length - 1 && <Divider sx={{ my: 2, bgcolor: 'white' }} />}
            </Box>
          ))}
          </Box>
        </SimpleBar>
      </Box>

      {/* Edit Modal */}
      <Dialog open={open} onClose={handleClose}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, pb: 0 }}>
          <DialogTitle>{t('Edit Experience')}</DialogTitle>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <ExperienceForm initialExperience={groupedValue as any} onCancel={handleClose} />
      </Dialog>
    </Box>
  );
};

export default Experience;
